import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../App';
import { api, useQuery } from '../api/client';
import type { Dashboard } from '../api/types';
import { Select } from '../components/Select';
import { BandPill, Card, Empty, ErrorNote, Score, Skeleton, Stat, VerdictPill, toneForScore } from '../components/ui';
import { formatPct } from '../lib/format';

const RANGES = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'All time', days: 0 },
];

/**
 * Horizontal bars.
 *
 * Magnitude is carried by LENGTH in a single hue; the row label carries
 * identity and the value is always printed. Colour is used for exactly one
 * thing - marking the reserved critical row - so nothing depends on telling
 * two similar hues apart.
 */
function BarRows({
  rows,
  max,
  format = (v: number) => String(v),
}: {
  rows: { label: string; value: number; critical?: boolean; title?: string; suffix?: string }[];
  max?: number;
  format?: (v: number) => string;
}) {
  const peak = max ?? Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="bar-rows">
      {rows.map((r) => (
        <div key={r.label} className={`bar-row ${r.critical ? 'is-critical' : ''}`} title={r.title}>
          <span className="bar-label">
            {r.label}
            {/* Never colour-alone: whatever the fill says is also said in text. */}
            {r.suffix && <span className="bar-suffix">{r.suffix}</span>}
          </span>
          <span className="bar-track">
            <span
              className={`bar-fill ${r.critical ? 'is-critical' : ''}`}
              style={{ width: `${Math.max(r.value > 0 ? 2 : 0, (r.value / peak) * 100)}%` }}
            />
          </span>
          <span className="bar-value">{format(r.value)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Score trend. One series, so no legend - the card title names it. A 2px line,
 * >=8px end marker, recessive gridlines, and every point carries a hover title.
 */
function TrendLine({ points }: { points: { day: string; averageScore: number | null; calls: number }[] }) {
  const data = points.filter((p) => p.averageScore !== null) as { day: string; averageScore: number; calls: number }[];
  if (data.length < 2) {
    return <p className="dim small">Not enough audited days in this range to show a trend.</p>;
  }

  const W = 100;
  const H = 100;
  const min = 1;
  const max = 5;
  const x = (i: number) => (i / (data.length - 1)) * W;
  const y = (v: number) => H - ((v - min) / (max - min)) * H;
  const path = data.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(2)} ${y(p.averageScore).toFixed(2)}`).join(' ');
  const last = data[data.length - 1]!;

  return (
    <>
      <svg className="spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Average QA score over time">
        {[2.5, 3.5, 4.5].map((g) => (
          <line key={g} x1="0" x2={W} y1={y(g)} y2={y(g)} stroke="var(--chart-grid)" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
        ))}
        <path d={path} fill="none" stroke="var(--chart-fill)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        {/* Hit areas, not markers. The viewBox is stretched to the container
            width, which would squash a <circle> into an oval - a rect stretches
            cleanly, and a full-height band is a bigger hover target than the
            mark itself, which is what we want anyway. */}
        {data.map((p, i) => {
          const half = W / (data.length - 1) / 2;
          return (
            <rect
              key={p.day}
              x={Math.max(0, x(i) - half)}
              y={0}
              width={half * 2}
              height={H}
              fill="transparent"
            >
              <title>{`${p.day}: ${p.averageScore.toFixed(1)} average across ${p.calls} call${p.calls === 1 ? '' : 's'}`}</title>
            </rect>
          );
        })}
      </svg>
      <div className="row small dim" style={{ justifyContent: 'space-between', marginTop: 6 }}>
        <span>{data[0]!.day}</span>
        <span>
          Latest <strong className={`score-${toneForScore(last.averageScore)}`}>{last.averageScore.toFixed(1)}</strong>
        </span>
        <span>{last.day}</span>
      </div>
      <p className="dim small mt-2">Gridlines mark the band boundaries: 2.5 Needs Work, 3.5 Good, 4.5 Excellent.</p>
    </>
  );
}

function DashboardSkeleton() {
  return (
    <div className="stack">
      <div className="grid grid-stats">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="stat">
            <Skeleton height={13} width={92} />
            <div style={{ height: 8 }} />
            <Skeleton height={26} width={64} />
          </div>
        ))}
      </div>
      <Skeleton height={220} />
    </div>
  );
}

export function DashboardPage() {
  const { user } = useSession();
  const [days, setDays] = useState(30);
  const isSdr = user?.role === 'sdr';

  const params = useMemo((): Record<string, string> => {
    if (!days) return {};
    return { from: new Date(Date.now() - days * 86_400_000).toISOString() };
  }, [days]);

  const { data, error, loading, reload } = useQuery<Dashboard>(() => api.dashboard(params), [JSON.stringify(params)]);

  const t = data?.totals;
  const hasAudits = (t?.callsAudited ?? 0) > 0;

  const accuracy =
    t && t.statusCompared > 0 ? Math.round(((t.statusCompared - t.statusMismatches) / t.statusCompared) * 100) : null;

  return (
    <>
      <header className="page-head">
        <div className="page-head-text">
          <h1>{isSdr ? 'My performance' : 'QA Dashboard'}</h1>
          <p className="page-sub">
            {isSdr
              ? 'Your audited calls, scores and coaching.'
              : 'Call quality, qualification accuracy and integrity across the team.'}
          </p>
        </div>
        <div className="page-head-actions">
          <Select
            id="range"
            ariaLabel="Date range"
            value={String(days)}
            onChange={(v) => setDays(Number(v))}
            options={RANGES.map((r) => ({ value: String(r.days), label: r.label }))}
          />
          {!isSdr && (
            <Link to="/calls/new" className="btn btn-primary">
              Submit a call
            </Link>
          )}
        </div>
      </header>

      <div className="page-body page-enter">
        <ErrorNote error={error} onRetry={reload} />

        {loading && !data ? (
          <DashboardSkeleton />
        ) : !hasAudits ? (
          <Card>
            <Empty
              title="No audited calls in this range"
              body={
                isSdr
                  ? 'Once your manager submits a call for audit it will appear here with its score, BANT verdict and coaching.'
                  : 'Submit a call with its transcript and the audit engine will score it against the SDR standard.'
              }
              action={
                !isSdr && (
                  <Link to="/calls/new" className="btn btn-primary">
                    Submit the first call
                  </Link>
                )
              }
            />
          </Card>
        ) : (
          <div className="stack">
            <div className="grid grid-stats">
              <Stat index={0} label="Calls audited" value={t!.callsAudited} meta={t!.pending ? `${t!.pending} still processing` : undefined} />
              <Stat index={1}
                label="Average QA score"
                value={t!.averageScore === null ? 'Not scored' : <Score value={t!.averageScore} />}
                meta="Average of scored categories, per call"
              />
              <Stat index={2}
                label="Qualified bookings"
                value={t!.qualifiedBookings}
                meta="B + A + N all PASS"
                hint="A meeting is a qualified booking only if Budget, Authority and Need all pass. Time frame flexes."
              />
              <Stat index={3}
                label="Mis-qualifications"
                value={t!.misQualifications}
                alert={t!.misQualifications > 0}
                meta="Logged qualified over a BANT failure"
              />
              <Stat index={4} label="Auto-fails" value={t!.autoFails} alert={t!.autoFails > 0} meta="Hard triggers, Section 6" />
              <Stat index={5}
                label="Status accuracy"
                value={accuracy === null ? 'No status logged' : formatPct(accuracy)}
                alert={accuracy !== null && accuracy < 80}
                meta={
                  accuracy === null
                    ? 'Nothing to reconcile'
                    : `${t!.statusMismatches} of ${t!.statusCompared} disagreed with the evidence`
                }
              />
              <Stat index={6} label="Integrity flags" value={t!.integrityFlags} alert={t!.integrityFlags > 0} meta="Across all audited calls" />
              {t!.failed > 0 && <Stat index={7} label="Failed to process" value={t!.failed} alert meta="Needs attention" />}
            </div>

            <div className="grid grid-2">
              <Card title="Band distribution" note={`${t!.callsAudited} calls`}>
                <BarRows
                  rows={data!.bands.map((b) => ({
                    label: b.band,
                    value: b.count,
                    critical: b.band === 'FAIL',
                    title: `${b.count} call${b.count === 1 ? '' : 's'} in the ${b.band} band`,
                  }))}
                  format={(v) => `${v} · ${t!.callsAudited ? Math.round((v / t!.callsAudited) * 100) : 0}%`}
                />
              </Card>

              <Card title="Average QA score over time">
                <TrendLine points={data!.trend} />
              </Card>
            </div>

            <Card title="Category averages" note="★ marks a critical category">
              <BarRows
                max={5}
                rows={data!.categories.map((c) => ({
                  label: `${c.critical ? '★ ' : ''}${c.letter}. ${c.title}`,
                  value: c.averageScore ?? 0,
                  // Red is reserved for the one event that changes an outcome:
                  // a CRITICAL category scored 1, which caps that call's band.
                  critical: c.critical && c.onesCount > 0,
                  suffix: c.onesCount > 0 ? `${c.onesCount}× scored 1` : undefined,
                  title:
                    c.averageScore === null
                      ? `${c.title}: never scored in this range`
                      : `${c.title}: ${c.averageScore} average across ${c.scoredCount} scored call${c.scoredCount === 1 ? '' : 's'}` +
                        (c.onesCount ? `, scored 1 on ${c.onesCount}` : '') +
                        (c.naCount ? `, N/A on ${c.naCount}` : ''),
                }))}
                format={(v) => (v ? v.toFixed(1) : '—')}
              />
              <p className="dim small mt-3">
                Bars run 1 to 5. A bar in red is a <strong>critical</strong> category that scored 1 on at least one call, which caps
                that call&rsquo;s band at Needs Work regardless of its average.
              </p>
            </Card>

            <div className="grid grid-2">
              <Card title="BANT outcomes" note="Independent of what the SDR logged">
                <div className="stack" style={{ gap: 14 }}>
                  {data!.bant.map((f) => {
                    const total = f.verdicts.reduce((n, v) => n + v.count, 0) || 1;
                    return (
                      <div key={f.factor}>
                        <div className="row mb-2" style={{ justifyContent: 'space-between' }}>
                          <strong style={{ textTransform: 'capitalize' }}>{f.factor === 'timeframe' ? 'Time frame' : f.factor}</strong>
                          <span className="row" style={{ gap: 5 }}>
                            {f.verdicts.map((v) => (
                              <span key={v.verdict} className="row" style={{ gap: 4 }}>
                                <VerdictPill verdict={v.verdict} size="sm" />
                                <span className="small tabular dim">{v.count}</span>
                              </span>
                            ))}
                          </span>
                        </div>
                        <div className="bar-track" aria-hidden>
                          <span
                            className="bar-fill"
                            style={{ width: `${((f.verdicts.find((v) => v.verdict === 'PASS')?.count ?? 0) / total) * 100}%` }}
                          />
                        </div>
                        <div className="small dim" style={{ marginTop: 3 }}>
                          {Math.round(((f.verdicts.find((v) => v.verdict === 'PASS')?.count ?? 0) / total) * 100)}% passed
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              <Card title="Integrity flags by type">
                {data!.integrityFlags.length === 0 ? (
                  <p className="dim">No integrity flags were raised on any call in this range.</p>
                ) : (
                  <BarRows
                    rows={data!.integrityFlags.map((f) => ({
                      label: f.label,
                      value: f.count,
                      critical: f.severity === 'high',
                      suffix: f.severity.toUpperCase(),
                      title: `${f.label} — ${f.severity} severity, ${f.count} occurrence${f.count === 1 ? '' : 's'}`,
                    }))}
                  />
                )}
              </Card>
            </div>

            {!isSdr && (
              <Card title="SDR performance" bodyless>
                <div className="table-wrap" style={{ border: 0, borderRadius: 0, boxShadow: 'none' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>SDR</th>
                        <th className="num">Audited</th>
                        <th className="num">Avg score</th>
                        <th>Band</th>
                        <th className="num">Qualified</th>
                        <th className="num">Mis-qual</th>
                        <th className="num">Auto-fails</th>
                        <th className="num">Flags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data!.sdrs.map((s) => (
                        <tr key={s.id}>
                          <td className="cell-primary">{s.name}</td>
                          <td className="num">{s.audited}</td>
                          <td className="num">
                            <Score value={s.averageScore} showMax={false} />
                          </td>
                          <td>
                            <BandPill
                              band={
                                s.averageScore === null
                                  ? null
                                  : s.averageScore >= 4.5
                                    ? 'Excellent'
                                    : s.averageScore >= 3.5
                                      ? 'Good'
                                      : s.averageScore >= 2.5
                                        ? 'Needs Work'
                                        : 'Poor'
                              }
                            />
                          </td>
                          <td className="num">{s.qualifiedBookings}</td>
                          <td className="num">{s.misQualifications > 0 ? <strong className="score-fail">{s.misQualifications}</strong> : 0}</td>
                          <td className="num">{s.autoFails > 0 ? <strong className="score-fail">{s.autoFails}</strong> : 0}</td>
                          <td className="num">{s.integrityFlags}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="dim small" style={{ padding: '11px 16px 0' }}>
                  Band here is derived from the SDR&rsquo;s average score across the range. It is not the same as a per-call band,
                  which can be capped or overridden by a critical 1 or an auto-fail.
                </p>
              </Card>
            )}
          </div>
        )}
      </div>
    </>
  );
}
